import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';

import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { Animations } from 'app/animations';
import { GenericObject } from 'ngx-web-api';
import { TranslateService } from '@ngx-translate/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Component({
  selector: 'fts-quantity-selector',
  templateUrl: './quantity-selector.component.html',
  styleUrls: ['./quantity-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [Animations.expanded],
})
export class QuantitySelectorComponent implements OnInit, OnChanges {
  @Input()
  quantity: number;
  @Input()
  minimumQuantity = 1;
  @Input()
  bubbleUpErrors = false;
  @Input()
  describedBy: string;

  @Output()
  quantityChange: EventEmitter<number> = new EventEmitter(true);
  @Output()
  quantityError: EventEmitter<GenericObject<string[]>> = new EventEmitter();
  @Output()
  previousQuantity: EventEmitter<number> = new EventEmitter();

  @ViewChild('quantityDecreaseBtn', { static: true })
  quantityDecreaseBtn: ElementRef;
  @ViewChild('quantityIncreaseBtn', { static: true })
  quantityIncreaseBtn: ElementRef;
  @ViewChild('quantityInput', { static: true })
  quantityInput: ElementRef;

  temporaryQuantity?: number;
  faMinus = faMinus;
  faPlus = faPlus;
  showMinWarning = false;

  private readonly maxQuantity = 99;
  private readonly debounceTime = 500;

  private quantityValue: Subject<{ quantity: number; warn: boolean }> = new Subject();

  constructor(private cdRef: ChangeDetectorRef, private translateService: TranslateService, private liveAnnouncer: LiveAnnouncer) {}

  ngOnInit(): void {
    this.temporaryQuantity = this.quantity;
    this.quantityValue
      .pipe(
        filter(q => !!q),
        debounceTime(this.debounceTime),
        distinctUntilChanged((prev, cur) => prev.quantity === cur.quantity)
      )
      .subscribe(({ quantity, warn }) => {
        if (quantity < this.minimumQuantity) {
          this.quantity = this.minimumQuantity;
          this.quantityChange.emit(this.minimumQuantity);
          this.checkValueAfterFocusOut(null);
          if (warn) {
            this.showMinValueWarning();
          }
          return;
        }
        if (this.showMinWarning) {
          this.showMinWarning = false;
        }
        this.quantityChange.emit(quantity);
      });

    this.subscribeToIncrease();
    this.subscribeToDecrease();
    this.setQuantityValue(this.quantity || 1, false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quantity']) {
      this.temporaryQuantity = this.quantity;
      this.quantityValue.next({ quantity: this.quantity, warn: false });
    }
  }

  quantityMask(rawValue: string) {
    if (/\D/.test(rawValue) && rawValue && rawValue.length <= 2) {
      return [/[1-9]/];
    } else if (rawValue && rawValue.length > 1) {
      return [/[1-9]/, /\d/];
    } else {
      return [/[1-9]/];
    }
  }

  setQuantityValue(quantity: string | number, warn = true) {
    const parsedQuantity = typeof quantity === 'string' ? parseInt(quantity, 10) : quantity;
    if (!quantity || isNaN(parsedQuantity)) {
      return;
    }
    this.quantityValue.next({ quantity: parsedQuantity, warn: warn });
  }

  private subscribeToIncrease(): void {
    const increase$ = fromEvent(this.quantityIncreaseBtn.nativeElement, 'click');
    increase$
      .pipe(
        filter(() => this.temporaryQuantity < this.maxQuantity),
        tap(() => {
          this.temporaryQuantity++;
          this.cdRef.detectChanges();
        }),
        debounceTime(this.debounceTime)
      )
      .subscribe(() => {
        this.liveAnnouncer.announce(
          this.translateService.instant('component.quantity_selector.increase_button_announcer', { quantity: this.temporaryQuantity }),
          1000
        );
        this.previousQuantity.emit(this.quantity);
        this.setQuantityValue(this.temporaryQuantity);
      });
  }

  private subscribeToDecrease(): void {
    const decrease$ = fromEvent(this.quantityDecreaseBtn.nativeElement, 'click');
    decrease$
      .pipe(
        filter(() => {
          const moreThanMin = !this.minimumQuantity || this.temporaryQuantity > this.minimumQuantity;
          if (!moreThanMin) {
            this.showMinValueWarning();
          }
          return moreThanMin;
        }),
        tap(() => {
          this.temporaryQuantity--;
          this.cdRef.detectChanges();
        }),
        debounceTime(this.debounceTime)
      )
      .subscribe(() => {
        this.previousQuantity.emit(this.quantity);
        this.setQuantityValue(this.temporaryQuantity);
        this.liveAnnouncer.announce(
          this.translateService.instant('component.quantity_selector.decrease_button_announcer', { quantity: this.temporaryQuantity }),
          1000
        );
      });
  }

  private showMinValueWarning() {
    if (this.bubbleUpErrors) {
      const minQError = this.translateService.instant('component.quantity_selector.min_warning_long', {
        minQuantity: this.minimumQuantity,
      });
      this.quantityError.emit({ minQuantity: [minQError] });
      setTimeout(() => this.quantityError.emit({ minQuantity: [] }), 3000);
      return;
    }
    this.showMinWarning = true;
    this.cdRef.detectChanges();
    setTimeout(() => ((this.showMinWarning = false), this.cdRef.detectChanges()), 3000);
  }

  checkValueAfterFocusOut(value: any) {
    if (!value) {
      this.quantityInput.nativeElement.value = this.quantity;
    }
  }
}
